/* @flow */
/* eslint no-control-regex: off */
import { List } from 'immutable'
import { withMessageFn, predicate, or, and } from './functions'
import { findDocuments } from '../helpers/document'
import { replaceWithObject } from '../helpers/string'
import { isValid, parseISO, isAfter, isEqual, isBefore } from 'date-fns'

import type { ValueSuccessType, ValidationType } from './index'
import type { MetaType } from '../helpers/document'
import type { TranslationType } from '../helpers/i18n'

export type DateType = Date | string | number
export type InnerDateType = Date | number
export type ValidatorReturnType = (value: any) => ValidationType

export const reportError = (value: Object, errorMessage: ?TranslationType) => {
  if (errorMessage && typeof errorMessage == 'string') {
    return replaceWithObject(value)(errorMessage)
  }
  return true
}
export const isExisty = (value: any): boolean => value !== null && value !== undefined
export const isEmpty = (value: any): boolean => value === ''
export const ISODateRegex = /^([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))$/
export const EmailRegex = /^((([a-z]|\d|[!#$%&'*+\-/=?^_`{|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#$%&'*+\-/=?^_`{|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i

/**
 * Validate a value according to a regex.
 * @param {RegExp} regexp - The RegExp to test for.
 * @return {function} A validation function.
 * @example
 * regexp(/[ABC]/)("A") == new Success("A")
 */
export const regexp = (regexp: RegExp, errorMessage: ?TranslationType = null) => {
  return withMessageFn(
    predicate((v: ValueSuccessType) => typeof v === 'string' && isExisty(v) && !isEmpty(v) && regexp.test(v)),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Check if the value is an email address.
 * @param {PrefixType} errorMessage custom errorMessage
 * @param {*} value The value to check.
 * @returns {Validation}
 * @example
 * email()('info@test.com') == Validation.Success('info@test.com')
 * @example
 * email()('info') == Validation.Failure(['"info" is not a valid E-Mail'])
 */
export const email = (errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(regexp(EmailRegex), (value: string) => reportError({ value }, errorMessage))
}

/**
 * Check if the value matches the regex
 * @param {regex}  regex custom regex
 * @param {PrefixType} errorMessage custom errorMessage
 * @param {string} value The value to check.
 * @returns {Validation}
 * @example
 * customRegex(/^[a-zA-Z.\-,&' ]+$/)('J. Bjorn-Nils & Sohn') == Validation.Success('J. Bjorn-Nils & Sohn')
 * @example
 * customRegex(/^[a-zA-Z.\-,&' ]+$/)('J. Björn') == Validation.Failure(['"J. Björn" did not match "/^[a-zA-Z.\\-,&\' ]+$/"'])
 */
export const customRegex = (regex: RegExp, errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(regexp(regex), (value: string) => reportError({ value }, errorMessage))
}

/**
 * Check if the value is a valid url.
 * @param {PrefixType} errorMessage custom errorMessage
 * @param {string} value The value to check.
 * @returns {Validation}
 * @example
 * url()('http://google.com') == Validation.Success('http://google.com')
 * @example
 * url()('google') == Validation.Failure(['"google" is not a valid URL'])
 */
export const url = (errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    regexp(
      /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www\.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w\-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[.!/\\\w]*))?)/
    ),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Check if the value is a phone number.
 * @param {PrefixType} errorMessage custom errorMessage
 * @param {string} value The value to check.
 * @returns {Validation}
 * @example
 * phone()('+41 79 271 02 56') == Validation.Success('+41 79 271 02 56')
 * phone()('0041 79 271 02 56') == Validation.Success('0041 79 271 02 56')
 * phone()('079 271 02 56') == Validation.Success('079 271 02 56')
 * phone()('+41792710256') == Validation.Success('+41792710256')
 * phone()('0041792710256') == Validation.Success('0041792710256')
 * phone()('0792710256') == Validation.Success('0792710256')
 * @example
 * phone()('1234') == Validation.Failure(['"1234" is not a valid phone number'])
 */
export const phone = (errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    regexp(/^([+]\d{1,2}|00\d{1,2}|0)(\s?)(\d{2})(\s?)(\d{3})(\s?)(\d{2})(\s?)(\d{2})$/),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Check if the value is a number.
 * @param {PrefixType} errorMessage custom errorMessage
 * @param {number} value The value to check.
 * @returns {Validation}
 * @example
 * number()(1) == Validation.Success(1)
 * @example
 * number()('foo') == number()('1') == Validation.Failure(['Should be a number'])
 */
export const number = (errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    predicate((v) => typeof v === 'number' && !isNaN(v)),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Check if the value is a number or a string containing a number.
 * @param {PrefixType} errorMessage custom errorMessage
 * @param {string|number} value The value to check.
 * @returns {Validation}
 * @example
 * numeric()(1) == numeric()('1') == Validation.Success(1)
 * @example
 * numeric()('foo') == Validation.Failure(['Should be a numeric value'])
 */
export const numeric = (errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(or(number(errorMessage), regexp(/^[-+]?(?:\d*[.])?\d+$/)), (value: string) =>
    reportError({ value }, errorMessage)
  )
}

/**
 * Check if the value is a boolean.
 * @param {PrefixType} errorMessage custom errorMessage
 * @param {boolean} value The value to check.
 * @returns {Validation}
 * @example
 * boolean()(true) == true == Validation.Success(1)
 * @example
 * boolean()('true') == Validation.Failure(['"true" should be a boolean value'])
 */
export const boolean = (errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    predicate((v) => Boolean(v) === v),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Check if the value is a non-empty string.
 * @param {PrefixType} errorMessage custom errorMessage
 * @param {*} - The value to check.
 * @returns {Validation}
 * @example
 * nonEmptyString()('content') == Validation.Success('content')
 * @example
 * nonEmptyString()('') == nonEmptyString()(1) == Validation.Failure(['Should be a non-empty string'])
 */
export const nonEmptyString = (errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    predicate((v) => typeof v === 'string' && v !== ''),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Check if the value length is shorter or equal to given value
 * @param {number} length max length of string
 * @param {PrefixType} errorMessage custom errorMessage
 * @param {*} - The value to check.
 * @returns {Validation}
 * @example
 * maxLength(4)('8303') == Validation.Success('content')
 * @example
 * maxLength(4)('content') == Validation.Failure(['exceeded the maxLength of "4"'])
 */
export const maxLength = (length: number, errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    predicate((v) => typeof v === 'string' && v.length <= length),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Check if the value length is longer or equal to given value
 * @param {number} length min length of string
 * @param {PrefixType} errorMessage custom errorMessage
 * @param {*} - The value to check.
 * @returns {Validation}
 * @example
 * maxLength(4)('8303') == Validation.Success('content')
 * @example
 * maxLength(4)('') == Validation.Failure(['not min "4" long'])
 */
export const minLength = (length: number, errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    predicate((v) => typeof v === 'string' && v.length >= length),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Check if the value is an empty string.
 * @param {PrefixType} errorMessage custom errorMessage
 * @param {*} - The value to check.
 * @returns {Validation}
 * @example
 * emptyString()('') == Validation.Success('')
 * @example
 * emptyString()('test') == emptyString()(1) == Validation.Failure(['Should be an empty string'])
 */
export const emptyString = (errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    predicate((v) => v === ''),
    (value: string) => reportError({ value }, errorMessage)
  )
}

export const isNull = (errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    predicate((v) => v === null || v === undefined),
    (value: string) => {
      return reportError({ value }, errorMessage)
    }
  )
}

export const empty = (errorMessage: ?TranslationType = null) => or(emptyString(errorMessage), isNull(errorMessage))

/**
 * Check if the value is of the given value.
 * @param {*} equalValue value that must to be equal
 * @param {PrefixType} errorMessage custom errorMessage
 * @param {*} - The value to check.
 * @returns {ValidationType}
 * @example
 * equals('content')('content') == Validation.Success('content')
 * @example
 * equals('hans')('jurg') == Validation.Failure(['Should be exactly "hans"'])
 */
export const equals = (equalValue: any, errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    predicate((value) => value === equalValue),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Sets an validator as optional
 * @param {function} validator the validator which is used if there is a value
 * @returns {ValidationType}
 * @example
 * optional(nonEmptyString()('')) == Validation.Success('')
 # optional(nonEmptyString()('test')) == Validation.Success('test')
 * @example
 * optional(email()('test')) == Validation.Failure(['"test" is not a valid E-Mail'])
 */
export const optional = (validator: (value: mixed) => ValidationType) => {
  return or(empty(), validator)
}

const parseDate = (value: DateType): InnerDateType => {
  if (typeof value == 'string') return parseISO(value)
  return value
}

const compareDate = (date: DateType, fn: (d1: InnerDateType, d2: InnerDateType) => boolean) => {
  return predicate((value: any) => {
    if (!value) return false
    return fn(parseDate(value), parseDate(date))
  })
}

/**
 * Check if the date value is higher than the supplied minimum date.
 * @param {string|number|Date} date the date string to check against
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @returns {ValidationType}
 * @example
 * (pivot)(value)
 * aboveDate('1985-01-01')('2000-01-01') == Validation.Success('2000-01-01')
 * @example
 * aboveDate('1985-01-01')('1980-01-01') == Validation.Failure(['Under the acceptable date'])
 */
export const aboveDate = (date: DateType, errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    compareDate(date, (date1: InnerDateType, date2: InnerDateType) => isAfter(date1, date2)),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Check if the date value is higher or equal than the supplied minimum date.
 * @param {string|number|Date} date the date string to check against
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @returns {ValidationType}
 * @example
 * (pivot)(value)
 * aboveEqualDate('2000-01-01')('2000-01-01') == Validation.Success('2000-01-01')
 * @example
 * aboveEqualDate('1985-01-01')('1980-01-01') == Validation.Failure(['Under the acceptable date'])
 */
export const aboveEqualDate = (date: DateType, errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    compareDate(date, (date1, date2) => isEqual(date1, date2) || isAfter(date1, date2)),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Check if the date value is higher than the supplied maximum date.
 * @param {string|number|Date} date the date string to check against
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @returns {ValidationType}
 * @example
 * belowDate('1985-01-01')('1980-01-01') == Validation.Success('1980-01-01')
 * @example
 * belowDate('1985-01-01')('2000-01-01') == Validation.Failure(['Above the acceptable date'])
 */
export const belowDate = (date: DateType, errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    compareDate(date, (date1, date2) => isBefore(date1, date2)),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Check if the date value is higher or equal than the supplied maximum date.
 * @param {string|number|Date} date the date string to check against
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @returns {ValidationType}
 * @example
 * belowEqualDate('1980-01-01')('1980-01-01') == Validation.Success('1980-01-01')
 * @example
 * belowEqualDate('1985-01-01')('2000-01-01') == Validation.Failure('Above the acceptable date')
 */
export const belowEqualDate = (date: DateType, errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    compareDate(date, (date1, date2) => isEqual(date1, date2) || isBefore(date1, date2)),
    (value: string) => reportError({ value }, errorMessage)
  )
}

/**
 * Check if the value is a valid date string
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {*} value The value to check.
 * @returns {ValidationType}
 * @example
 * validDate()('1980-01-01') == Validation.Success('1980-01-01')
 * @example
 * validDate()(null) == Validation.Failure(['Invalid date'])
 */
export const validDate = (errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    predicate((v: any) => {
      return isValid(parseDate(v))
    }),
    (value: string) => reportError({ value }, errorMessage)
  )
}

export const validISODate = (errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return and(regexp(ISODateRegex, errorMessage), validDate(errorMessage))
}

/**
 * Check if the value is a valid time string
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {*} value The value to check.
 * @returns {ValidationType}
 * @example
 * validTime()('12:00') == Validation.Success('12:00')
 * @example
 * validTime()(null) == Validation.Failure(['Invalid Time'])
 */
export const validTime = (errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(regexp(/^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/), (value: string) =>
    reportError({ value }, errorMessage)
  )
}

/**
 * Check if a number is lower than another number to compare with
 * @param {number} valueToCompareWith value that must be lower than another number
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {number} value the value to check
 * @returns {ValidationType}
 * @example
 * lowerThan(10)(1) == Validation.Success(1)
 * @example
 * lowerThan(10)(11) == Validation.Failure('Should be < 10')
 */
export const lowerThan = (
  valueToCompareWith: ValueSuccessType,
  errorMessage: ?TranslationType = null
): ValidatorReturnType => {
  return withMessageFn(
    predicate(
      (value: ValueSuccessType) =>
        typeof valueToCompareWith === 'number' && typeof value === 'number' && value < valueToCompareWith
    ),
    (value: string) => reportError({ value, should: JSON.stringify(valueToCompareWith) }, errorMessage)
  )
}

/**
 * Check if a number is lower than or equal to another number to compare with
 * @param {number} valueToCompareWith value that must be lower than or equal to another number
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {number} value the value to check
 * @returns {ValidationType}
 * @example
 * lowerThanOrEqualTo(10)(1) == Validation.Success(1)
 * @example
 * lowerThanOrEqualTo(10)(11) == Validation.Failure('Should be <= 10')
 */
export const lowerThanOrEqualTo = (
  valueToCompareWith: ValueSuccessType,
  errorMessage: ?TranslationType = null
): ValidatorReturnType => {
  return withMessageFn(
    predicate(
      (value: ValueSuccessType) =>
        typeof valueToCompareWith === 'number' && typeof value === 'number' && value <= valueToCompareWith
    ),
    (value: string) => reportError({ value, should: JSON.stringify(valueToCompareWith) }, errorMessage)
  )
}

/**
 * Check if a number is greater than another number to compare with
 * @param {number} valueToCompareWith value that must be greater than another number
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {number} value the value to check
 * @returns {ValidationType}
 * @example
 * greaterThan(1)(10) == Validation.Success(10)
 * @example
 * greaterThan(10)(1) == Validation.Failure('Should be > 10')
 */
export const greaterThan = (
  valueToCompareWith: ValueSuccessType,
  errorMessage: ?TranslationType = null
): ValidatorReturnType => {
  return withMessageFn(
    predicate(
      (value: ValueSuccessType) =>
        typeof valueToCompareWith === 'number' && typeof value === 'number' && value > valueToCompareWith
    ),
    (value: string) => reportError({ value, should: JSON.stringify(valueToCompareWith) }, errorMessage)
  )
}

/**
 * Check if a number is greater than or equal to another number to compare with
 * @param {number} valueToCompareWith value that must be greater than or equal to another number
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {number} value the value to check
 * @returns {ValidationType}
 * @example
 * greaterThanOrEqualTo(1)(10) == Validation.Success(10)
 * @example
 * greaterThanOrEqualTo(10)(1) == Validation.Failure('Should be >= 10')
 */
export const greaterThanOrEqualTo = (
  valueToCompareWith: ValueSuccessType,
  errorMessage: ?TranslationType = null
): ValidatorReturnType => {
  return withMessageFn(
    predicate(
      (value: ValueSuccessType) =>
        typeof valueToCompareWith === 'number' && typeof value === 'number' && value >= valueToCompareWith
    ),
    (value: string) => reportError({ value, should: JSON.stringify(valueToCompareWith) }, errorMessage)
  )
}

/**
 * Check if the value is included in the given array
 * @param {array} arr array possible values
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {string|object|number|boolean} value The value to check.
 * @returns {ValidationType}
 * @example
 * includedIn(['x', 'y'])('x') == Validation.Success('x')
 * @example
 * includedIn(['x', 'y'])('z') == Validation.Failure('z not in x,y')
 */
export const includedIn = (arr: Array<string | number>, errorMessage: ?TranslationType = null): ValidatorReturnType => {
  return withMessageFn(
    predicate((value: ValueSuccessType) => arr.includes(value)),
    (value: string) => reportError({ value: JSON.stringify(value), should: JSON.stringify(arr) }, errorMessage)
  )
}

/**
 * Check if the value has the given Array.length/List.size
 * @param {number} length length of the Array|List
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {Array|List} value The value to check.
 * @returns {ValidationType}
 * @example
 * arrayLength(1)([1]) == Validation.Success([1])
 * @example
 * arrayLength(2)([1]) == Validation.Failure('Array|List length 1 does not match 2')
 */
export const arrayLength = (length: number, errorMessage: ?TranslationType = null): ValidatorReturnType => {
  const fn = withMessageFn(
    predicate((value: ValueSuccessType) => {
      // $FlowFixMe
      return (Array.isArray(value) && value.length == length) || (List.isList(value) && value.size == length)
    }),
    (value: Array<*> | List<*>) => reportError({ value: List(value).size, should: length }, errorMessage)
  )
  // $FlowFixMe
  fn.arrayCheck = true
  return fn
}

/**
 * Check if the value has the min given Array.length/List.size
 * @param {number} min minimum length of the Array|List
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {Array|List} value The value to check.
 * @returns {ValidationType}
 * @example
 * arrayLengthMin(1)([1]) == Validation.Success([1])
 * @example
 * arrayLengthMin(2)([1]) == Validation.Failure('Array|List length 1 less than 2')
 */
export const arrayLengthMin = (min: number, errorMessage: ?TranslationType = null): ValidatorReturnType => {
  const fn = withMessageFn(
    predicate((value: ValueSuccessType) => {
      // $FlowFixMe
      return (Array.isArray(value) && value.length >= min) || (List.isList(value) && value.size >= min)
    }),
    (value: Array<*> | List<*>) => reportError({ value: List(value).size, should: min }, errorMessage)
  )
  // $FlowFixMe
  fn.arrayCheck = true
  return fn
}

/**
 * Check if the value has the max given Array.length/List.size
 * @param {number} max maximum length of the Array|List
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {Array|List} value The value to check.
 * @returns {ValidationType}
 * @example
 * arrayLengthMax(1)([1]) == Validation.Success([1])
 * @example
 * arrayLengthMax(0)([1]) == Validation.Failure('Array|List length 1 more than 0')
 */
export const arrayLengthMax = (max: number, errorMessage: ?TranslationType = null): ValidatorReturnType => {
  const fn = withMessageFn(
    predicate((value: ValueSuccessType) => {
      // $FlowFixMe
      return (Array.isArray(value) && value.length <= max) || (List.isList(value) && value.size <= max)
    }),
    (value: Array<*> | List<*>) => reportError({ value: List(value).size, should: max }, errorMessage)
  )
  // $FlowFixMe
  fn.arrayCheck = true
  return fn
}

/**
 * Wrapper validator for arrayLength, to check if the value has the given length for the documents collection
 * @param {number} length length of the documents collection
 * @param {object} meta object with any properties that classify/filter specific items in the documents collection
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {Array|List} value The value to check.
 * @returns {ValidationType}
 * @example
 * arrayLength(1, { step: 'person:0', field: 'residence_permit' })([1]) == Validation.Success([1])
 * @example
 * arrayLength(2, { step: 'person:0', field: 'residence_permit' })([1]) == Validation.Failure('Array|List length 1 does not match 2')
 */
export const documentsLength = (length: number, meta: MetaType, errorMessage: ?TranslationType = null) => {
  const fn = (value: any) => {
    return arrayLength(length, errorMessage)(findDocuments(value)(meta))
  }
  // $FlowFixMe
  fn.arrayCheck = true
  return fn
}

/**
 * Wrapper validator for arrayLengthMin, to check if the value has the min length for the documents collection
 * @param {number} min minimum length of the documents collection
 * @param {object} meta object with any properties that classify/filter specific items in the collection
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {Array|List} value The value to check.
 * @returns {ValidationType}
 * @example
 * documentsLengthMin(1, { step: 'person:0', field: 'residence_permit' })([1]) == Validation.Success([1])
 * @example
 * documentsLengthMin(2, { step: 'person:0', field: 'residence_permit' })([1]) == Validation.Failure('documents length is less than 2')
 */
export const documentsLengthMin = (min: number, meta: MetaType, errorMessage: ?TranslationType = null) => {
  const fn = (value: any) => {
    return arrayLengthMin(min, errorMessage)(findDocuments(value)(meta))
  }
  // $FlowFixMe
  fn.arrayCheck = true
  return fn
}

/**
 * Wrapper validator for arrayLengthMax , to check if the value has the max length for the documents collection
 * @param {number} max maximum length of the documents collection
 * @param {object} meta object with any properties that classify/filter specific documents in the collection
 * @param {PrefixType} errorMessage optional custom errorMessage
 * @param {Array|List} value The value to check.
 * @returns {ValidationType}
 * @example
 * documentsLengthMax(1, { step: 'person:0', field: 'residence_permit' })([1]) == Validation.Success([1])
 * @example
 * documentsLengthMax(0, { step: 'person:0', field: 'residence_permit' })([1]) == Validation.Failure('documents length is more than 0')
 */
export const documentsLengthMax = (max: number, meta: MetaType, errorMessage: ?TranslationType = null) => {
  const fn = (value: any) => {
    return arrayLengthMax(max, errorMessage)(findDocuments(value)(meta))
  }
  // $FlowFixMe
  fn.arrayCheck = true
  return fn
}
